import { usePagination } from "@vega-api/custom/utils/usePagination";
import { type DeviceLogin } from "@vega-api/generated/types/deviceLogin";
import { type DeviceLoginDeviceType } from "@vega-api/generated/types/deviceLoginDeviceType";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";

import {
  getDeviceIcon,
  useDeviceLoginColumns,
} from "../hooks/useDeviceLoginColumns";
import { type useDeviceLoginData } from "../hooks/useDeviceLoginData";
import { formatUserAgent, transformDeviceLogin } from "../utils/deviceUtils";

type DeviceDetailsProps = {
  device: DeviceLogin & { formattedLocation: string };
  showActive?: boolean;
};

const DeviceDetails = ({ device, showActive }: DeviceDetailsProps) => {
  const icon = getDeviceIcon(device.deviceType as DeviceLoginDeviceType);

  return (
    <div className="flex gap-3 rounded-default bg-text/secondary/10 p-3">
      <div className="flex items-center">{icon}</div>
      <div className="flex flex-col items-start">
        <div className="font-semibold">{formatUserAgent(device.userAgent)}</div>
        <div className="text-text/secondary">{device.formattedLocation}</div>
        {showActive && device.isActive ? (
          <Tooltip content={m.settings_activeDeviceTooltip()}>
            <Tag variant={TagVariants.Positive}>{m.common_active()}</Tag>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

export { DeviceDetails };

type DeviceLoginTableProps = {
  deviceLoginData: ReturnType<typeof useDeviceLoginData>;
};

export const DeviceLoginTable = ({
  deviceLoginData,
}: DeviceLoginTableProps) => {
  const {
    loginHistory,
    meta,
    isLoading,
    currentPage,
    dateRange,
    handlePageChange,
    currentPageSize,
    setCurrentPageSize,
  } = deviceLoginData;

  const transformedLoginHistory = loginHistory.map(transformDeviceLogin);

  const { paginationElement } = usePagination({
    meta,
    currentPage,
    setCurrentPage: handlePageChange,
    isLoading,
    currentPageSize,
    setCurrentPageSize,
    filterDependencies: [dateRange],
  });

  const columns = useDeviceLoginColumns();

  return (
    <CoreTable
      columns={columns}
      data={transformedLoginHistory}
      dataTestId="device-login-table"
      emptyState={{
        title: m.settings_noDevicesFound(),
      }}
      isLoading={isLoading}
      paginationElement={paginationElement}
    />
  );
};
